import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useNotifications = (userId?: string) => {
  const queryClient = useQueryClient();

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications', userId],
    queryFn: async (): Promise<any[]> => {
      if (!userId) return [];
      
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
  });

  const unreadCount = notifications.filter((n: any) => !n.is_read).length;

  // Realtime Subscription
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel('schema-db-changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen for INSERT (new notif) and UPDATE (read status)
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          // Optimistically update or refetch
          queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
          
          if (payload.eventType === 'INSERT') {
            const newNotif = payload.new as any;
            toast.info(newNotif.title, {
              description: newNotif.message,
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);

  const markAsRead = async (notificationId: string) => {
    // Optimistic update
    queryClient.setQueryData(['notifications', userId], (old: any[]) => {
      if (!old) return [];
      return old.map((n: any) => n.id === notificationId ? { ...n, is_read: true } : n);
    });

    const { error } = await (supabase.from('notifications') as any)
      .update({ is_read: true })
      .eq('id', notificationId);

    if (error) {
      toast.error("שגיאה בעדכון התראה");
      queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
    }
  };

  const markAllAsRead = async () => {
    // Optimistic update
    queryClient.setQueryData(['notifications', userId], (old: any[]) => {
      if (!old) return [];
      return old.map((n: any) => ({ ...n, is_read: true }));
    });

    const { error } = await (supabase.from('notifications') as any)
      .update({ is_read: true })
      .eq('user_id', userId)
      .eq('is_read', false);

    if (error) {
      toast.error("שגיאה בעדכון התראות");
      queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
    }
  };

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead
  };
};